import {useCallback, useEffect, useState} from "react";
import axios from "axios";
import {Box, Paper, Popover} from "@mui/material";
import {CamerasWrapper} from "./cameras-wrapper";

const parseCameras = (input) => {
    const lines = input.split("\n");
    lines.splice(0, 2);
    lines.splice(lines.length - 1, 1);
    return lines.map((x) => {
        const values = x.split("\t");
        let i = 1;
        const lastIndexOfSlash = values[0].lastIndexOf("/");
        return {
            filename: values[0].substring(lastIndexOfSlash + 1, values[0].length),
            x: values[i++],
            y: values[i++],
            z: values[i++],
            omega: values[i++],
            phi: values[i++],
            kappa: values[i++],
        };
    });
};

export const Cameras = ({potreeViewer, data}) => {
    const [cameras, setCameras] = useState([]);

    useEffect(() => {
        if (!potreeViewer || !data?.camerasUrl) return;
        const getCameras = async () => {
            const res = await axios.get(data.camerasUrl);
            setCameras(parseCameras(res.data));
        };
        getCameras().catch(console.log);
    }, [potreeViewer, data]);

    const [photoPopover, setPhotoPopover] = useState({
        open: false,
        left: 0,
        top: 0,
        src: "",
        thumbnailSrc: "",
    });

    const showPhotoDialog = useCallback((path, thumbnailPath, mousePos) => {
        setPhotoPopover({
            left: mousePos[0],
            top: mousePos[1],
            open: true,
            src: path,
            thumbnailSrc: thumbnailPath,
        });
    }, []);

    useEffect(() => {
        if (!potreeViewer || !cameras || !data?.camerasBaseUrl) return;
        const orientedImages = [];
        const sceneNode = new window.Potree.Object3D();
        sceneNode.name = "photos";
        for (const camera of cameras) {
            const imagePath = `${data.camerasBaseUrl}/${camera.filename}`;
            const imageThumbnailPath = `${imagePath}-300`;
            let orientedImage = new window.Potree.OrientedImage(imageThumbnailPath);
            orientedImage.path = imagePath;
            let position = [camera.x, camera.y, camera.z];
            let rotation = [camera.omega, camera.phi, camera.kappa];
            let dimension = [300, 300];
            orientedImage.set(position, rotation, dimension);
            sceneNode.add(orientedImage.mesh);
            orientedImages.push(orientedImage);
        }
        potreeViewer.scene.scene.add(sceneNode);
        const onClick = (evt) => {
            const rect = potreeViewer.renderer.domElement.getBoundingClientRect();
            const [x, y] = [evt.clientX, evt.clientY];
            const onClickPosition = new window.Potree.Vector2(
                (x - rect.left) / rect.width,
                (y - rect.top) / rect.height
            );
            //const intersects = getIntersects(onClickPosition, scene.children);
            const camera = potreeViewer.scene.getActiveCamera();
            const mouse = new window.Potree.Vector3(
                +(onClickPosition.x * 2) - 1,
                -(onClickPosition.y * 2) + 1
            );
            const raycaster = new window.Potree.Raycaster();
            raycaster.setFromCamera(mouse, camera);
            const intersects = raycaster.intersectObjects(
                orientedImages.map((i) => i.mesh)
            );
            if (intersects.length > 0) {
                const intersection = intersects[0];
                if (!intersection.object.orientedImage.mesh.visible)
                    return;
                evt.preventDefault();
                showPhotoDialog(
                    intersection.object.orientedImage.path,
                    intersection.object.orientedImage.id,
                    [x, y]
                );
            }
        };
        potreeViewer.renderer.domElement.addEventListener("click", onClick, false);
        potreeViewer.scene.dispatchEvent({
            type: "camera_images_added",
            data: new CamerasWrapper(orientedImages),
        });

        const update = () => {
            for(const image of orientedImages){
                const {width, height} = image;
                const aspect = width / height;

                const camera = potreeViewer.scene.getActiveCamera();

                const imgPos = image.mesh.getWorldPosition(new window.Potree.Vector3());
                const camPos = camera.position;
                const d = camPos.distanceTo(imgPos)/20;

                image.mesh.scale.set(d * aspect, d, 1);
                image.mesh.material.uniforms.uNear.value = camera.near;
            }

        };
        potreeViewer.addEventListener("update", update);
    }, [potreeViewer, cameras, data, showPhotoDialog]);

    const handlePhotoPopoverClose = () => {
        setPhotoPopover((x) => ({
            ...x,
            open: false,
        }));
    };

    return <Popover
        anchorReference="anchorPosition"
        open={photoPopover.open}
        onClose={handlePhotoPopoverClose}
        anchorPosition={{top: photoPopover.top, left: photoPopover.left}}
        anchorOrigin={{
            vertical: 'top',
            horizontal: 'center',
        }}
        transformOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
        }}
    >
        <Paper sx={{p: 2, lineHeight: 0}}>
            <a href={photoPopover.src} target="_blank">
                <Box component="img" src={photoPopover.thumbnailSrc}/>
            </a>
        </Paper>
    </Popover>
};
